type MessageFile = {
	default: { [key: string]: string };
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getMessagesForLocale = async (locale: string): Promise<Record<string, string>> => {
	switch (locale) {
		case 'cs': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-cs" */ '../../i18n/cs'
			).then((mod) => mod.default);
		}
		case 'da': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-da" */ '../../i18n/da'
			).then((mod) => mod.default);
		}
		case 'de': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-de" */ '../../i18n/de'
			).then((mod) => mod.default);
		}
		case 'en': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en" */ '../../i18n/en'
			).then((mod) => mod.default);
		}
		case 'en_GB': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en_GB" */ '../../i18n/en_GB'
			).then((mod) => mod.default);
		}
		case 'en_ZZ': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en_ZZ" */ '../../i18n/en_ZZ'
			).then((mod) => mod.default);
		}
		case 'es': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-es" */ '../../i18n/es'
			).then((mod) => mod.default);
		}
		case 'fi': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-fi" */ '../../i18n/fi'
			).then((mod) => mod.default);
		}
		case 'fr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-fr" */ '../../i18n/fr'
			).then((mod) => mod.default);
		}
		case 'hr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-hr" */ '../../i18n/hr'
			).then((mod) => mod.default);
		}
		case 'hu': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-hu" */ '../../i18n/hu'
			).then((mod) => mod.default);
		}
		case 'it': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-it" */ '../../i18n/it'
			).then((mod) => mod.default);
		}
		case 'ja': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ja" */ '../../i18n/ja'
			).then((mod) => mod.default);
		}
		case 'ko': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ko" */ '../../i18n/ko'
			).then((mod) => mod.default);
		}
		case 'nb': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-nb" */ '../../i18n/nb'
			).then((mod) => mod.default);
		}
		case 'nl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-nl" */ '../../i18n/nl'
			).then((mod) => mod.default);
		}
		case 'pl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-pl" */ '../../i18n/pl'
			).then((mod) => mod.default);
		}
		case 'pt_BR':
		case 'pt-BR': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-pt_BR" */ '../../i18n/pt_BR'
			).then((mod) => mod.default);
		}
		case 'ru': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ru" */ '../../i18n/ru'
			).then((mod) => mod.default);
		}
		case 'sl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sl" */ '../../i18n/sl'
			).then((mod) => mod.default);
		}
		case 'sr_RS': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sr_RS" */ '../../i18n/sr_RS'
			).then((mod) => mod.default);
		}
		case 'sr_YR': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sr_YR" */ '../../i18n/sr_YR'
			).then((mod) => mod.default);
		}
		case 'sv': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sv" */ '../../i18n/sv'
			).then((mod) => mod.default);
		}
		case 'th': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-th" */ '../../i18n/th'
			).then((mod) => mod.default);
		}
		case 'tr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-tr" */ '../../i18n/tr'
			).then((mod) => mod.default);
		}
		case 'uk': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-uk" */ '../../i18n/uk'
			).then((mod) => mod.default);
		}
		case 'vi': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-vi" */ '../../i18n/vi'
			).then((mod) => mod.default);
		}
		case 'zh_TW': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-zh_TW" */ '../../i18n/zh_TW'
			).then((mod) => mod.default);
		}
		case 'zh': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-zh" */ '../../i18n/zh'
			).then((mod) => mod.default);
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getMessagesForLocale(fragments[0]);
			} else {
				return import(
					/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en" */ '../../i18n/en'
				).then((mod) => mod.default);
			}
		}
	}
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getSearchPageMessagesForLocale = async (locale: string): Promise<MessageFile> => {
	switch (locale) {
		case 'cs': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-cs" */ '../../i18n/cs'
			);
		}
		case 'da': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-da" */ '../../i18n/da'
			);
		}
		case 'de': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-de" */ '../../i18n/de'
			);
		}
		case 'en': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en" */ '../../i18n/en'
			);
		}
		case 'en_GB': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en_GB" */ '../../i18n/en_GB'
			);
		}
		case 'en_ZZ': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en_ZZ" */ '../../i18n/en_ZZ'
			);
		}
		case 'es': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-es" */ '../../i18n/es'
			);
		}
		case 'fi': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-fi" */ '../../i18n/fi'
			);
		}
		case 'fr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-fr" */ '../../i18n/fr'
			);
		}
		case 'hr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-hr" */ '../../i18n/hr'
			);
		}
		case 'hu': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-hu" */ '../../i18n/hu'
			);
		}
		case 'it': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-it" */ '../../i18n/it'
			);
		}
		case 'ja': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ja" */ '../../i18n/ja'
			);
		}
		case 'ko': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ko" */ '../../i18n/ko'
			);
		}
		case 'nb': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-nb" */ '../../i18n/nb'
			);
		}
		case 'nl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-nl" */ '../../i18n/nl'
			);
		}
		case 'pl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-pl" */ '../../i18n/pl'
			);
		}
		case 'pt_BR':
		case 'pt-BR': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-pt_BR" */ '../../i18n/pt_BR'
			);
		}
		case 'ru': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-ru" */ '../../i18n/ru'
			);
		}
		case 'sl': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sl" */ '../../i18n/sl'
			);
		}
		case 'sr_RS': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sr_RS" */ '../../i18n/sr_RS'
			);
		}
		case 'sr_YR': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sr_YR" */ '../../i18n/sr_YR'
			);
		}
		case 'sv': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-sv" */ '../../i18n/sv'
			);
		}
		case 'th': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-th" */ '../../i18n/th'
			);
		}
		case 'tr': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-tr" */ '../../i18n/tr'
			);
		}
		case 'uk': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-uk" */ '../../i18n/uk'
			);
		}
		case 'vi': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-vi" */ '../../i18n/vi'
			);
		}
		case 'zh_TW': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-zh_TW" */ '../../i18n/zh_TW'
			);
		}
		case 'zh': {
			return import(
				/* webpackChunkName: "@atlaskit-internal_search-page-i18n-zh" */ '../../i18n/zh'
			);
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getSearchPageMessagesForLocale(fragments[0]);
			} else {
				return import(
					/* webpackChunkName: "@atlaskit-internal_search-page-i18n-en" */ '../../i18n/en'
				);
			}
		}
	}
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getSearchCommonMessagesForLocale = async (locale: string): Promise<MessageFile> => {
	switch (locale) {
		case 'cs': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-cs" */
				'@atlassian/search-common/src/i18n/cs'
			);
		}
		case 'da': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-da" */
				'@atlassian/search-common/src/i18n/da'
			);
		}
		case 'de': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-de" */
				'@atlassian/search-common/src/i18n/de'
			);
		}
		case 'en': {
			return import(
				/* webpackChunkName: "search-common-translations-en" */
				'@atlassian/search-common/src/i18n/en'
			);
		}
		case 'en_GB': {
			return import(
				/* webpackChunkName: "search-common-translations-en_GB" */
				'@atlassian/search-common/src/i18n/en_GB'
			);
		}
		case 'en_ZZ': {
			return import(
				/* webpackChunkName: "search-common-translations-en-ZZ" */
				'@atlassian/search-common/src/i18n/en_ZZ'
			);
		}
		case 'es': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-es" */
				'@atlassian/search-common/src/i18n/es'
			);
		}
		case 'fi': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-fi" */
				'@atlassian/search-common/src/i18n/fi'
			);
		}
		case 'fr': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-fr" */
				'@atlassian/search-common/src/i18n/fr'
			);
		}
		case 'hr': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-hr" */
				'@atlassian/search-common/src/i18n/hr'
			);
		}
		case 'hu': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-hu" */
				'@atlassian/search-common/src/i18n/hu'
			);
		}
		case 'it': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-it" */
				'@atlassian/search-common/src/i18n/it'
			);
		}
		case 'ja': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-ja" */
				'@atlassian/search-common/src/i18n/ja'
			);
		}
		case 'ko': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-ko" */
				'@atlassian/search-common/src/i18n/ko'
			);
		}
		case 'nb': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-nb" */
				'@atlassian/search-common/src/i18n/nb'
			);
		}
		case 'nl': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-nl" */
				'@atlassian/search-common/src/i18n/nl'
			);
		}
		case 'pl': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-pl" */
				'@atlassian/search-common/src/i18n/pl'
			);
		}
		case 'pt_BR':
		case 'pt-BR': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-pt_BR" */
				'@atlassian/search-common/src/i18n/pt_BR'
			);
		}
		case 'ru': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-ru" */
				'@atlassian/search-common/src/i18n/ru'
			);
		}
		case 'sl': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-sl" */
				'@atlassian/search-common/src/i18n/sl'
			);
		}
		case 'sr_RS': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-sr_RS" */
				'@atlassian/search-common/src/i18n/sr_RS'
			);
		}
		case 'sr_YR': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-sr_YR" */
				'@atlassian/search-common/src/i18n/sr_YR'
			);
		}
		case 'sv': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-sv" */
				'@atlassian/search-common/src/i18n/sv'
			);
		}
		case 'th': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-th" */
				'@atlassian/search-common/src/i18n/th'
			);
		}
		case 'tr': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-tr" */
				'@atlassian/search-common/src/i18n/tr'
			);
		}
		case 'uk': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-uk" */
				'@atlassian/search-common/src/i18n/uk'
			);
		}
		case 'vi': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-vi" */
				'@atlassian/search-common/src/i18n/vi'
			);
		}
		case 'zh_TW': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-zh-TW" */
				'@atlassian/search-common/src/i18n/zh_TW'
			);
		}
		case 'zh': {
			return import(
				/* webpackChunkName: "search-common-translations-i18n-zh" */
				'@atlassian/search-common/src/i18n/zh'
			);
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getSearchCommonMessagesForLocale(fragments[0]);
			} else {
				return import(
					/* webpackChunkName: "search-common-translations-en" */
					'@atlassian/search-common/src/i18n/en'
				);
			}
		}
	}
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getSearchUiMessagesForLocale = async (locale: string): Promise<MessageFile> => {
	switch (locale) {
		case 'cs': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-cs" */
				'@atlassian/search-ui/src/i18n/cs'
			);
		}
		case 'da': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-da" */
				'@atlassian/search-ui/src/i18n/da'
			);
		}
		case 'de': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-de" */
				'@atlassian/search-ui/src/i18n/de'
			);
		}
		case 'en': {
			return import(
				/* webpackChunkName: "search-ui-translations-en" */
				'@atlassian/search-ui/src/i18n/en'
			);
		}
		case 'en_GB': {
			return import(
				/* webpackChunkName: "search-ui-translations-en_GB" */
				'@atlassian/search-ui/src/i18n/en_GB'
			);
		}
		case 'en_ZZ': {
			return import(
				/* webpackChunkName: "search-ui-translations-en-ZZ" */
				'@atlassian/search-ui/src/i18n/en_ZZ'
			);
		}
		case 'es': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-es" */
				'@atlassian/search-ui/src/i18n/es'
			);
		}
		case 'fi': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-fi" */
				'@atlassian/search-ui/src/i18n/fi'
			);
		}
		case 'fr': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-fr" */
				'@atlassian/search-ui/src/i18n/fr'
			);
		}
		case 'hr': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-hr" */
				'@atlassian/search-ui/src/i18n/hr'
			);
		}
		case 'hu': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-hu" */
				'@atlassian/search-ui/src/i18n/hu'
			);
		}
		case 'it': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-it" */
				'@atlassian/search-ui/src/i18n/it'
			);
		}
		case 'ja': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-ja" */
				'@atlassian/search-ui/src/i18n/ja'
			);
		}
		case 'ko': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-ko" */
				'@atlassian/search-ui/src/i18n/ko'
			);
		}
		case 'nb': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-nb" */
				'@atlassian/search-ui/src/i18n/nb'
			);
		}
		case 'nl': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-nl" */
				'@atlassian/search-ui/src/i18n/nl'
			);
		}
		case 'pl': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-pl" */
				'@atlassian/search-ui/src/i18n/pl'
			);
		}
		case 'pt_BR':
		case 'pt-BR': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-pt_BR" */
				'@atlassian/search-ui/src/i18n/pt_BR'
			);
		}
		case 'ru': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-ru" */
				'@atlassian/search-ui/src/i18n/ru'
			);
		}
		case 'sl': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-sl" */
				'@atlassian/search-ui/src/i18n/sl'
			);
		}
		case 'sr_RS': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-sr_RS" */
				'@atlassian/search-ui/src/i18n/sr_RS'
			);
		}
		case 'sr_YR': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-sr_YR" */
				'@atlassian/search-ui/src/i18n/sr_YR'
			);
		}
		case 'sv': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-sv" */
				'@atlassian/search-ui/src/i18n/sv'
			);
		}
		case 'th': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-th" */
				'@atlassian/search-ui/src/i18n/th'
			);
		}
		case 'tr': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-tr" */
				'@atlassian/search-ui/src/i18n/tr'
			);
		}
		case 'uk': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-uk" */
				'@atlassian/search-ui/src/i18n/uk'
			);
		}
		case 'vi': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-vi" */
				'@atlassian/search-ui/src/i18n/vi'
			);
		}
		case 'zh_TW': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-zh-TW" */
				'@atlassian/search-ui/src/i18n/zh_TW'
			);
		}
		case 'zh': {
			return import(
				/* webpackChunkName: "search-ui-translations-i18n-zh" */
				'@atlassian/search-ui/src/i18n/zh'
			);
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getSearchUiMessagesForLocale(fragments[0]);
			} else {
				return import(
					/* webpackChunkName: "search-ui-translations-en" */
					'@atlassian/search-ui/src/i18n/en'
				);
			}
		}
	}
};

/**
 * Tries to get the most specific messages bundle for a given locale.
 *
 * Strategy:
 * 1. Try to find messages with the exact string (i.e. 'fr_FR')
 * 2. If that doesn't work, try to find messages for the country locale (i.e. 'fr')
 * 3. If that doesn't work, return english messages as a fallback.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export const getSearchAiMessagesForLocale = async (locale: string): Promise<MessageFile> => {
	switch (locale) {
		case 'cs': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-cs" */
				'@atlassian/search-ai/src/i18n/cs'
			);
		}
		case 'da': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-da" */
				'@atlassian/search-ai/src/i18n/da'
			);
		}
		case 'de': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-de" */
				'@atlassian/search-ai/src/i18n/de'
			);
		}
		case 'en': {
			return import(
				/* webpackChunkName: "search-ai-translations-en" */
				'@atlassian/search-ai/src/i18n/en'
			);
		}
		case 'en_GB': {
			return import(
				/* webpackChunkName: "search-ai-translations-en_GB" */
				'@atlassian/search-ai/src/i18n/en_GB'
			);
		}
		case 'en_ZZ': {
			return import(
				/* webpackChunkName: "search-ai-translations-en-ZZ" */
				'@atlassian/search-ai/src/i18n/en_ZZ'
			);
		}
		case 'es': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-es" */
				'@atlassian/search-ai/src/i18n/es'
			);
		}
		case 'fi': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-fi" */
				'@atlassian/search-ai/src/i18n/fi'
			);
		}
		case 'fr': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-fr" */
				'@atlassian/search-ai/src/i18n/fr'
			);
		}
		case 'hr': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-hr" */
				'@atlassian/search-ai/src/i18n/hr'
			);
		}
		case 'hu': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-hu" */
				'@atlassian/search-ai/src/i18n/hu'
			);
		}
		case 'it': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-it" */
				'@atlassian/search-ai/src/i18n/it'
			);
		}
		case 'ja': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-ja" */
				'@atlassian/search-ai/src/i18n/ja'
			);
		}
		case 'ko': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-ko" */
				'@atlassian/search-ai/src/i18n/ko'
			);
		}
		case 'nb': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-nb" */
				'@atlassian/search-ai/src/i18n/nb'
			);
		}
		case 'nl': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-nl" */
				'@atlassian/search-ai/src/i18n/nl'
			);
		}
		case 'pl': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-pl" */
				'@atlassian/search-ai/src/i18n/pl'
			);
		}
		case 'pt_BR':
		case 'pt-BR': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-pt_BR" */
				'@atlassian/search-ai/src/i18n/pt_BR'
			);
		}
		case 'ru': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-ru" */
				'@atlassian/search-ai/src/i18n/ru'
			);
		}
		case 'sl': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-sl" */
				'@atlassian/search-ai/src/i18n/sl'
			);
		}
		case 'sr_RS': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-sr_RS" */
				'@atlassian/search-ai/src/i18n/sr_RS'
			);
		}
		case 'sr_YR': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-sr_YR" */
				'@atlassian/search-ai/src/i18n/sr_YR'
			);
		}
		case 'sv': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-sv" */
				'@atlassian/search-ai/src/i18n/sv'
			);
		}
		case 'th': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-th" */
				'@atlassian/search-ai/src/i18n/th'
			);
		}
		case 'tr': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-tr" */
				'@atlassian/search-ai/src/i18n/tr'
			);
		}
		case 'uk': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-uk" */
				'@atlassian/search-ai/src/i18n/uk'
			);
		}
		case 'vi': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-vi" */
				'@atlassian/search-ai/src/i18n/vi'
			);
		}
		case 'zh_TW': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-zh-TW" */
				'@atlassian/search-ai/src/i18n/zh_TW'
			);
		}
		case 'zh': {
			return import(
				/* webpackChunkName: "search-ai-translations-i18n-zh" */
				'@atlassian/search-ai/src/i18n/zh'
			);
		}
		default: {
			const fragments = locale.split(/[_-]/);
			if (fragments.length === 2) {
				return getSearchAiMessagesForLocale(fragments[0]);
			} else {
				return import(
					/* webpackChunkName: "search-ai-translations-en" */
					'@atlassian/search-ai/src/i18n/en'
				);
			}
		}
	}
};

/**
 * Fetches the messages from all packages for a locale.
 *
 * @param locale string specifying the locale like 'en_GB', or 'fr'.
 */
export function fetchLanguageForPackages(locale: string): Promise<{ [key: string]: string }> {
	return Promise.allSettled([
		getSearchPageMessagesForLocale(locale),
		getSearchCommonMessagesForLocale(locale),
		getSearchUiMessagesForLocale(locale),
		getSearchAiMessagesForLocale(locale),
	]).then((results) => {
		const accumulatedMessages = results.reduce((acc, result) => {
			if (result.status === 'fulfilled') {
				return { ...acc, ...result.value.default };
			}
			return acc;
		}, {});
		return accumulatedMessages;
	});
}
